import { computed, inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import moment from 'moment';
import { catchError, map } from 'rxjs/operators';
import { UserService } from '../auth/user.service';
import { ToastController } from '@ionic/angular';
import { ByDayCollection } from '../../models/collections.model';
import { AgendaJourCrPartiel } from '../../models/agenda/agenda-jour.model';
import { environment } from '../../../../environments/environment';
import { AgendaCrPartiel } from '../../models/agenda/agenda-cr.model';
import { TypeActivite } from '../../models/agenda/activite.model';
import { NetworkProvider, NetworkStatus } from '../network/network';
import { toSignal } from '@angular/core/rxjs-interop';

export interface CrFilter {
  idIntervenant: string;
  dateDebutIntervention: string;
  dateFinIntervention: string;
}

@Injectable({ providedIn: 'root' })
export class AgendaCrServicePartiel {
  private readonly userService: UserService = inject(UserService);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly toastCtrl: ToastController = inject(ToastController);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly networkProvider = inject(NetworkProvider);

  private readonly networkStatus = toSignal(this.networkProvider.networkStatusSubject);
  readonly isOnline = computed(() => this.networkStatus() === NetworkStatus.ONLINE);

  constructor() {}

  getAgendaCrPartiel(displayedDate: Date): Observable<ByDayCollection<AgendaJourCrPartiel>> {
    const dateDebut = moment(displayedDate).subtract(7, 'day').toISOString();
    const dateFin = moment(displayedDate).add(7, 'day').toISOString();

    const url = `${environment.apis.API_DACTECH_BACK_ROOT}/v2/crs/listCrsForAgenda`;
    let filter: CrFilter = {
      dateDebutIntervention: dateDebut,
      dateFinIntervention: dateFin,
      idIntervenant: this.userService.getId(),
    };

    return this.http.post<AgendaCrPartiel[]>(url, filter).pipe(
      map((result) => {
        return this.mapperResponse(result);
      }),
      catchError(() => {
        if (this.isOnline()) {
          this.presentToast(this.translate.instant('AGENDA.CR.TOAST.ERROR')).then();
        }
        return of(new ByDayCollection<AgendaJourCrPartiel>());
      })
    );
  }

  private mapperResponse(data: AgendaCrPartiel[]): ByDayCollection<AgendaJourCrPartiel> {
    let response: ByDayCollection<AgendaJourCrPartiel> = new ByDayCollection<AgendaJourCrPartiel>();
    for (let crResponse of data) {
      let date = moment(crResponse.intervention_date_debut);

      let agenda: AgendaJourCrPartiel = { crs: [] };
      if (response.has(date)) {
        agenda = response.get(date);
      }

      let cr: AgendaCrPartiel = {
        ...crResponse,
        dateDebut: new Date(crResponse.intervention_date_debut),
        dateFin: new Date(crResponse.intervention_date_fin),
        type: TypeActivite.CrPartiel,
      };

      agenda.crs.push(cr);
      response.put(date, agenda);
    }
    return response;
  }

  private async presentToast(message: string) {
    let toast = await this.toastCtrl.create({
      message: message,
      duration: 5000,
    });
    toast.present().then();
  }
}
